import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { useTableFilters } from '../utils/useTableFilters';
import { deleteWorkOrderForm, getPaginatedForms, createWorkOrderForm } from '../api/work-order-formsAPI';
import FormHistoryDataTable from './FormHistoryDataTable';
import FormViewModal from './FormViewModal';
import WorkOrderShare from './WorkOrderShare';
import { getForms, getFormById } from '../api/formsAPI';
import { createFormData } from '../api/form-dataAPI';
import {
  createWorkOrderFormAction,
  deleteWorkOrderFormAction,
  getWorkOrderFormActionsByWorkOrderFormId,
} from '../api/work-order-form-actionsAPI';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useModule } from '../utils/useModule';

const INITIAL_FORMS_PAGE_SIZE = 25;
const INCREMENTAL_FORMS_PAGE_SIZE = 5;

const FormHistoryLayer = () => {
  const [activeTab, setActiveTab] = useState('completed');

  const userProfile = useUserProfile();

  const [completedForms, setCompletedForms] = useState([]);
  const [completedTotalRows, setCompletedTotalRows] = useState(0);
  const [completedPending, setCompletedPending] = useState(false);
  const [completedPage, setCompletedPage] = useState(1);
  const [inProgressForms, setInProgressForms] = useState([]);
  const [inProgressTotalRows, setInProgressTotalRows] = useState(0);
  const [inProgressPending, setInProgressPending] = useState(false);
  const [inProgressPage, setInProgressPage] = useState(1);
  const [assignedForms, setAssignedForms] = useState([]);
  const [assignedTotalRows, setAssignedTotalRows] = useState(0);
  const [assignedPending, setAssignedPending] = useState(false);
  const [assignedPage, setAssignedPage] = useState(1);
  const [search, setSearch] = useState('');
  const [viewFormId, setViewFormId] = useState(null);
  const [formToDelete, setFormToDelete] = useState(null);
  const [deletePending, setDeletePending] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const canDeleteForms = userProfile?.privileges?.includes('work-order-forms:delete');
  const [showStartFormModal, setShowStartFormModal] = useState(false);
  const [startFormModalMode, setStartFormModalMode] = useState('start');
  const [formsCatalog, setFormsCatalog] = useState([]);
  const [formsCatalogLoading, setFormsCatalogLoading] = useState(false);
  const [selectedFormToStart, setSelectedFormToStart] = useState(null);
  const [showConfirmStartModal, setShowConfirmStartModal] = useState(false);
  const [creatingNewForm, setCreatingNewForm] = useState(false);
  const [formsCatalogError, setFormsCatalogError] = useState(null);
  const [formsCatalogSearch, setFormsCatalogSearch] = useState('');
  const [formsCatalogActiveType, setFormsCatalogActiveType] = useState('all');
  const [startFormError, setStartFormError] = useState(null);
  const [showShareFormModal, setShowShareFormModal] = useState(false);
  const [selectedFormToShare, setSelectedFormToShare] = useState(null);
  const planthireEnabled = useModule('planthire');
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);

  const tableContainerRef = useRef(null);

  const getNextLazyLoadPage = (loadedCount) => (
    Math.floor((Math.max(loadedCount, INITIAL_FORMS_PAGE_SIZE) - INITIAL_FORMS_PAGE_SIZE) / INCREMENTAL_FORMS_PAGE_SIZE) + 6
  );


  const tableFilters = useMemo(
    () => [
      {
        field: 'assetId',
        type: 'dropDown',
        label: 'Asset',
        api: 'assets/for-dropdown',
        menuBarFilter: 1,
        displayField: (a) => `${a.assetNumber || ''} ${a.assetDescription || ''}`.trim(),
      },
      { field: 'areaId', type: 'dropDown', label: 'Area', api: 'areas', displayField: 'name' },
      { field: 'userId', type: 'dropDown', label: 'Completed By', api: 'users', displayField: 'name' },
      { field: 'formTypeId', type: 'dropDown', label: 'Form Type', api: 'form-type', menuBarFilter: 1, displayField: 'name' },
      {
        field: 'updatedByUserId',
        type: 'dropDown',
        label: 'Updated By',
        api: 'users',
        displayField: 'name',
        menuBarFilter: 1,
      },
      {
        field: 'departmentId',
        type: 'dropDown',
        label: 'Department',
        api: 'hierarchy-structures',
        displayField: 'name',
      },
    ],
    [],
  );

  const {
    appliedFilterParams,
    renderFilterControls,
  } = useTableFilters(tableFilters, {}, {
    onApply: () => {
      setCompletedPage(1);
      setInProgressPage(1);
      setAssignedPage(1);
    },
    onReset: () => {
      setSearch('');
      setCompletedPage(1);
      setInProgressPage(1);
      setAssignedPage(1);
    },
  });

  const fetchCompletedForms = async ({ append = false } = {}) => {
    setCompletedPending(true);
    try {
      const resp = await getPaginatedForms({
        page: append ? getNextLazyLoadPage(completedForms.length) : 1,
        pageSize: append ? INCREMENTAL_FORMS_PAGE_SIZE : INITIAL_FORMS_PAGE_SIZE,
        search,
        assetId: appliedFilterParams.assetId,
        areaId: appliedFilterParams.areaId,
        departmentId: appliedFilterParams.departmentId,
        userId: appliedFilterParams.userId,
        updatedByUserId: appliedFilterParams.updatedByUserId,
        formTypeId: appliedFilterParams.formTypeId,
        completed: 1,
        sortField: sortField || undefined,
        sortOrder: sortOrder || undefined,
      });
      setCompletedForms((prev) => (append ? [...prev, ...(resp.data || [])] : (resp.data || [])));
      setCompletedTotalRows(resp.total);
    } catch (err) {
      console.error('Error loading completed forms', err);
      setCompletedForms([]);
      setCompletedTotalRows(0);
    } finally {
      setCompletedPending(false);
    }
  };

  const fetchInProgressForms = async (pageOverride, { append = false } = {}) => {
    setInProgressPending(true);
    try {
      const resp = await getPaginatedForms({
        page: pageOverride ?? (append ? getNextLazyLoadPage(inProgressForms.length) : 1),
        pageSize: append ? INCREMENTAL_FORMS_PAGE_SIZE : INITIAL_FORMS_PAGE_SIZE,
        search,
        assetId: appliedFilterParams.assetId,
        areaId: appliedFilterParams.areaId,
        departmentId: appliedFilterParams.departmentId,
        userId: appliedFilterParams.userId,
        updatedByUserId: appliedFilterParams.updatedByUserId,
        formTypeId: appliedFilterParams.formTypeId,
        inProgress: 1,
        sortField: sortField || undefined,
        sortOrder: sortOrder || undefined,
      });
      setInProgressForms((prev) => (append ? [...prev, ...(resp.data || [])] : (resp.data || [])));
      setInProgressTotalRows(resp.total);
    } catch (err) {
      console.error('Error loading in progress forms', err);
      setInProgressForms([]);
      setInProgressTotalRows(0);
    } finally {
      setInProgressPending(false);
    }
  };

  const fetchAssignedForms = async (pageOverride, { append = false } = {}) => {
    setAssignedPending(true);
    try {
      const resp = await getPaginatedForms({
        page: pageOverride ?? (append ? getNextLazyLoadPage(assignedForms.length) : 1),
        pageSize: append ? INCREMENTAL_FORMS_PAGE_SIZE : INITIAL_FORMS_PAGE_SIZE,
        search,
        assetId: appliedFilterParams.assetId,
        areaId: appliedFilterParams.areaId,
        departmentId: appliedFilterParams.departmentId,
        userId: appliedFilterParams.userId,
        updatedByUserId: appliedFilterParams.updatedByUserId,
        formTypeId: appliedFilterParams.formTypeId,
        completed: 0,
        assigned: 1,
        inProgress: 0,
        sortField: sortField || undefined,
        sortOrder: sortOrder || undefined,
      });
      const filteredData = Array.isArray(resp.data)
        ? resp.data.filter((row) => Number(row?.assigned) === 1 && Number(row?.inProgress) === 0)
        : [];
      setAssignedForms((prev) => (append ? [...prev, ...filteredData] : filteredData));
      setAssignedTotalRows((prevTotal) => (append ? prevTotal + filteredData.length : filteredData.length));
    } catch (err) {
      console.error('Error loading assigned forms', err);
      setAssignedForms([]);
      setAssignedTotalRows(0);
    } finally {
      setAssignedPending(false);
    }
  };

  const openFormSelectionModal = async (mode = 'start') => {
    setStartFormModalMode(mode);
    setSelectedFormToShare(null);
    setShowStartFormModal(true);
    setFormsCatalogLoading(true);
    setFormsCatalog([]);
    setFormsCatalogError(null);
    setFormsCatalogSearch('');
    setFormsCatalogActiveType('all');
    setSelectedFormToStart(null);
    setShowConfirmStartModal(false);
    setStartFormError(null);
    try {
      const forms = await getForms();
      setFormsCatalog(Array.isArray(forms) ? forms : []);
    } catch (err) {
      console.error('Error loading available forms', err);
      setFormsCatalog([]);
      setFormsCatalogError('Unable to load forms. Please try again.');
    } finally {
      setFormsCatalogLoading(false);
    }
  };

  const openStartNewFormModal = async () => openFormSelectionModal('start');

  const openShareFormModal = async () => openFormSelectionModal('share');

  const closeStartFormModal = () => {
    if (creatingNewForm) return;
    setShowStartFormModal(false);
    setSelectedFormToStart(null);
    setSelectedFormToShare(null);
    setShowConfirmStartModal(false);
    setStartFormError(null);
  };

  const handleSelectFormToStart = (form) => {
    if (creatingNewForm) return;
    setSelectedFormToStart(form);
    setShowConfirmStartModal(true);
    setStartFormError(null);
  };

  const handleSelectFormToShare = (form) => {
    if (creatingNewForm) return;
    setSelectedFormToShare(form);
    setShowStartFormModal(false);
    setShowShareFormModal(true);
  };

  const formsCatalogTypes = useMemo(() => {
    if (!formsCatalog.length) return [];
    const unique = new Map();
    formsCatalog.forEach((form) => {
      const name = form.formType?.name || form.category?.name || 'Uncategorised';
      if (!unique.has(name)) {
        unique.set(name, name);
      }
    });
    return Array.from(unique.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [formsCatalog]);

  useEffect(() => {
    if (formsCatalogActiveType === 'all') return;
    if (!formsCatalogTypes.includes(formsCatalogActiveType)) {
      setFormsCatalogActiveType('all');
    }
  }, [formsCatalogActiveType, formsCatalogTypes]);

  const filteredFormsCatalog = useMemo(() => {
    const term = formsCatalogSearch.trim().toLowerCase();
    return formsCatalog
      .filter((form) => {
        if (formsCatalogActiveType === 'all') return true;
        const formTypeName = form.formType?.name || form.category?.name || 'Uncategorised';
        return formTypeName === formsCatalogActiveType;
      })
      .filter((form) => {
        if (!term) return true;
        const nameMatch = form.name?.toLowerCase().includes(term);
        const formTypeName = form.formType?.name || form.category?.name || '';
        const formTypeMatch = formTypeName.toLowerCase().includes(term);
        return nameMatch || formTypeMatch;
      });
  }, [formsCatalog, formsCatalogSearch, formsCatalogActiveType]);

  const handleCancelConfirmStart = () => {
    if (creatingNewForm) return;
    setShowConfirmStartModal(false);
    setSelectedFormToStart(null);
    setStartFormError(null);
  };

  const handleCreateNewForm = async () => {
    if (!selectedFormToStart) return;
    setStartFormError(null);
    setCreatingNewForm(true);
    let newWorkOrderForm = null;
    try {
      const userId = userProfile?.id ?? null;
      newWorkOrderForm = await createWorkOrderForm({
        formId: selectedFormToStart.id,
        workOrderId: null,
        assetId: null,
        locationId: 0,
        createdBy: userId,
        assigned: 1,
        assignedAt: new Date().toISOString(),
        assignedToName: userProfile?.name ?? null,
        assignUserId: userId,
      });

      const formDetails = await getFormById(selectedFormToStart.id);
      if (!formDetails) {
        throw new Error('Unable to load form details');
      }

      const items = formDetails?.tblFormItems || [];
      await Promise.all(
        items.map((item) =>
          createFormData({
            formItemId: item.id,
            formId: item.formId,
            workOrderFormId: newWorkOrderForm.id,
            description: item.description,
            width: item.width,
            images: item.images,
            heading: item.heading,
            formInputTypeId: item.formInputTypeId,
            instructions: item.instructions,
            includePhoto: item.includePhoto,
            disableComments: item.disableComments,
            raiseDefects: item.raiseDefects,
            required: item.required,
            data: item.data || null,
            sortOrder: item.sortOrder,
            assetId: null,
            departmentId: null,
            teamId: null,
            createdBy: userId,
          }),
        ),
      );

      const actions = formDetails?.tblFormActions || [];
      await Promise.all(
        actions.map((action) =>
          createWorkOrderFormAction({
            workOrderFormId: newWorkOrderForm.id,
            actionData: action.actionData,
            formActionId: action.formActionId,
            createdBy: userId,
          }),
        ),
      );

      setShowConfirmStartModal(false);
      setShowStartFormModal(false);
      setSelectedFormToStart(null);
      setActiveTab('inProgress');
      setInProgressPage(1);
      setViewFormId(newWorkOrderForm.id);
      await fetchInProgressForms(1);
    } catch (err) {
      console.error('Error creating new work order form', err);
      if (newWorkOrderForm?.id) {
        try {
          await deleteWorkOrderForm(newWorkOrderForm.id);
        } catch (cleanupErr) {
          console.error('Error rolling back new work order form', cleanupErr);
        }
      }
      setStartFormError('Unable to start form. Please try again.');
    } finally {
      setCreatingNewForm(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'completed') {
      fetchCompletedForms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, completedPage, search, appliedFilterParams, sortField, sortOrder]);

  useEffect(() => {
    if (activeTab === 'inProgress') {
      fetchInProgressForms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, inProgressPage, search, appliedFilterParams, sortField, sortOrder]);

  useEffect(() => {
    if (activeTab === 'assigned') {
      fetchAssignedForms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, assignedPage, search, appliedFilterParams, sortField, sortOrder]);

  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
  }, []);

  const handleRequestDelete = (form) => {
    if (!canDeleteForms) return;
    setFormToDelete(form);
  };

  const handleConfirmDelete = async () => {
    if (!formToDelete) return;
    setDeletePending(true);
    try {
      const actions = await getWorkOrderFormActionsByWorkOrderFormId(formToDelete.id);
      if (Array.isArray(actions) && actions.length) {
        await Promise.all(actions.map((action) => deleteWorkOrderFormAction(action.id)));
      }
      await deleteWorkOrderForm(formToDelete.id);
      setFormToDelete(null);
      await fetchInProgressForms();
    } catch (err) {
      console.error('Error deleting work order form', err);
    } finally {
      setDeletePending(false);
    }
  };

  const handleCancelDelete = () => {
    if (deletePending) return;
    setFormToDelete(null);
  };

  const handleSortChange = (field) => {
    setCompletedPage(1);
    setInProgressPage(1);
    setAssignedPage(1);
    if (sortField === field) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else if (sortOrder === 'desc') {
        setSortField(null);
        setSortOrder(null);
      } else {
        setSortOrder('asc');
      }
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };


  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const hasMore = activeTab === 'completed'
      ? completedForms.length < completedTotalRows
      : activeTab === 'inProgress'
        ? inProgressForms.length < inProgressTotalRows
        : assignedForms.length < assignedTotalRows;

    const isLoading = activeTab === 'completed'
      ? completedPending
      : activeTab === 'inProgress'
        ? inProgressPending
        : assignedPending;

    const onScroll = () => {
      if (container.scrollTop + container.clientHeight < container.scrollHeight - 5 || isLoading || !hasMore) return;

      if (activeTab === 'completed') {
        fetchCompletedForms({ append: true });
      } else if (activeTab === 'inProgress') {
        fetchInProgressForms(undefined, { append: true });
      } else {
        fetchAssignedForms(undefined, { append: true });
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [activeTab, completedForms.length, completedPending, completedTotalRows, inProgressForms.length, inProgressPending, inProgressTotalRows, assignedForms.length, assignedPending, assignedTotalRows]);

  const handleCloseViewForm = async () => {
    setViewFormId(null);
    if (activeTab === 'completed') {
      await fetchCompletedForms();
    } else if (activeTab === 'assigned') {
      await fetchAssignedForms();
    } else {
      await fetchInProgressForms();
    }
  };

  return (
    <>
      {headerSearchContainer &&
        createPortal(
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: '200px' }}
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCompletedPage(1);
              setInProgressPage(1);
              setAssignedPage(1);
            }}
          />,
          headerSearchContainer,
        )}
      <div className="card h-100 p-0 rounded-0">
        <div className="card-header d-flex justify-content-between align-items-center border-0 pb-16">
          <div className="d-flex flex-wrap align-items-center gap-2">
            {renderFilterControls()}
          </div>
          <div className="d-flex align-items-center gap-2 ms-auto me-8">
            {/*<button className="btn text-primary" onClick={openShareFormModal}>
              <Icon
                icon="mdi:share-variant-outline"
                className="me-8"
                style={{ position: 'relative', top: '0px', fontSize: '24px' }}
              />
              <span style={{ position: 'relative', top: '-1px' }}>Assign To Contractor</span>
            </button>*/}
            <button
              type="button"
              className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
              onClick={openStartNewFormModal}
            >
              <Icon icon="mdi:plus" className="text-lg" />
              Start New Form
            </button>
          </div>
        </div>
        <div className="card-body p-0 d-flex flex-column">
          <ul className="nav nav-tabs mb-3 px-16">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'completed' ? 'active' : ''}`}
                onClick={() => setActiveTab('completed')}
              >
                Completed Forms
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'inProgress' ? 'active' : ''}`}
                onClick={() => setActiveTab('inProgress')}
              >
                In Progress Forms
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'assigned' ? 'active' : ''}`}
                onClick={() => setActiveTab('assigned')}
              >
                Assigned
              </button>
            </li>
          </ul>
          <div ref={tableContainerRef} className="table-responsive flex-grow-1" style={{ height: "calc(100vh - 314px)" }}>
            {activeTab === 'completed' ? (
              <FormHistoryDataTable
                forms={completedForms}
                loading={completedPending}
                onView={(row) => setViewFormId(row.id)}
                planthireEnabled={planthireEnabled}
                variant="completed"
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleSortChange}
              />
            ) : activeTab === 'inProgress' ? (
              <FormHistoryDataTable
                forms={inProgressForms}
                loading={inProgressPending}
                onView={(row) => setViewFormId(row.id)}
                variant="in-progress"
                onDelete={handleRequestDelete}
                canDelete={canDeleteForms}
                planthireEnabled={planthireEnabled}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleSortChange}
              />
            ) : (
              <FormHistoryDataTable
                forms={assignedForms}
                loading={assignedPending}
                onView={(row) => setViewFormId(row.id)}
                variant="assigned"
                planthireEnabled={planthireEnabled}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleSortChange}
              />
            )}
          </div>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 px-16 py-8 border-top">
            {(() => {
              const currentPage =
                activeTab === 'completed'
                  ? completedPage
                  : activeTab === 'assigned'
                    ? assignedPage
                    : inProgressPage;
              const activePageClasses =
                'page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md bg-primary-600 text-white';

              return (
                <>
                  <span className="text-sm">
                  </span>
                  <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center mb-0 me-48">
                    <li className="page-item">
                      <span className={activePageClasses}>{currentPage}</span>
                    </li>
                  </ul>
                </>
              );
            })()}
          </div>
        </div>
        {showStartFormModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xxl modal-dialog-centered">
              {/* 🔑 Make modal-content a flex column so header/body/footer stack and body can grow */}
              <div className="modal-content d-flex flex-column" style={{ height: "75vh" }}>
                <div className="modal-header">
                  <h5 className="modal-title">{startFormModalMode === 'share' ? 'Select Form to Share' : 'Start New Form'}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeStartFormModal}
                    disabled={creatingNewForm}
                  />
                </div>

                {/* 🔑 Body grows and can contain its own scroll area */}
                <div className="modal-body d-flex flex-column flex-grow-1 overflow-hidden" style={{ minHeight: 0 }}>
                  {formsCatalogLoading ? (
                    <div className="text-center py-4">
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    </div>
                  ) : formsCatalogError ? (
                    <div className="alert alert-danger mb-0" role="alert">
                      {formsCatalogError}
                    </div>
                  ) : formsCatalog.length === 0 ? (
                    <div className="text-center text-muted py-4">No forms available.</div>
                  ) : (
                    <>
                      {/* Header/filters take natural height */}
                      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mt-8 mb-36 pb-24 border-bottom">
                        <p className="mb-0 text-muted">
                          Select a form to start. This creates a new in-progress form and opens it for completion.
                        </p>
                        <div className="ms-md-auto" style={{ minWidth: "240px" }}>
                          <input
                            type="search"
                            className="form-control"
                            placeholder="Search forms"
                            value={formsCatalogSearch}
                            onChange={(event) => setFormsCatalogSearch(event.target.value)}
                            disabled={creatingNewForm}
                          />
                        </div>
                      </div>

                      {filteredFormsCatalog.length === 0 ? (
                        <div className="text-center text-muted py-4">No forms match the current search.</div>
                      ) : (
                        <div
                          className="d-flex flex-column flex-lg-row gap-3 flex-grow-1 overflow-hidden"
                          style={{ minHeight: 0 }}
                        >
                          <div className="filter-sidebar flex-shrink-0 mb-3 mb-lg-0">
                            <div className="nav flex-lg-column gap-2" role="tablist" aria-orientation="vertical">
                              <button
                                type="button"
                                className={`filter-sidebar__button ${formsCatalogActiveType === 'all' ? 'is-active' : ''}`}
                                aria-pressed={formsCatalogActiveType === 'all'}
                                onClick={() => setFormsCatalogActiveType('all')}
                                disabled={creatingNewForm}
                              >
                                All
                              </button>
                              {formsCatalogTypes.map((typeName) => (
                                <button
                                  key={typeName}
                                  type="button"
                                  className={`filter-sidebar__button ${formsCatalogActiveType === typeName ? 'is-active' : ''}`}
                                  aria-pressed={formsCatalogActiveType === typeName}
                                  onClick={() => setFormsCatalogActiveType(typeName)}
                                  disabled={creatingNewForm}
                                >
                                  {typeName}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="flex-grow-1 overflow-auto">
                            <div className="row g-3 w-100">
                              {filteredFormsCatalog.map((form) => {
                                const imageUrl = form.image
                                  ? `${process.env.REACT_APP_SPACES_BASE}/${form.image}`
                                  : `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`;
                                return (
                                  <div key={form.id} className="col-sm-6 col-md-4 col-xl-3">
                                    <button
                                      type="button"
                                      className="p-0 border-0 bg-transparent w-100 text-start"
                                      onClick={() => (startFormModalMode === 'share' ? handleSelectFormToShare(form) : handleSelectFormToStart(form))}
                                      disabled={creatingNewForm}
                                    >
                                      <div className="hover-scale-img border radius-10 overflow-hidden position-relative h-100">
                                        <div className="max-h-266-px overflow-hidden">
                                          <img
                                            src={imageUrl}
                                            alt={form.name}
                                            className="hover-scale-img__img w-100 h-100 form-card-image-height"
                                            style={{ height: '140px', objectFit: 'cover' }}
                                          />
                                        </div>
                                        <div className="mt-12 pb-16 px-24">
                                          <div className="fw-semibold text-md text-truncate">{form.name}</div>
                                          <div className="text-sm text-muted text-truncate">
                                            {form.formType?.name || 'Uncategorised'}
                                          </div>
                                        </div>
                                      </div>
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeStartFormModal}
                    disabled={creatingNewForm}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>

        )}
        {startFormModalMode === 'start' && showConfirmStartModal && selectedFormToStart && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog modal-sm modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Start New Form</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCancelConfirmStart}
                    disabled={creatingNewForm}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    This will create a new Assigned form. Do you wish to continue?
                  </p>
                  {startFormError && (
                    <div className="alert alert-danger mt-3 mb-0" role="alert">
                      {startFormError}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancelConfirmStart}
                    disabled={creatingNewForm}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCreateNewForm}
                    disabled={creatingNewForm}
                  >
                    {creatingNewForm ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Creating...
                      </>
                    ) : (
                      'OK'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {viewFormId && (
          <FormViewModal
            workOrderFormId={viewFormId}
            onClose={handleCloseViewForm}
          />
        )}
        {showShareFormModal && selectedFormToShare && (
          <WorkOrderShare
            show={showShareFormModal}
            form={selectedFormToShare}
            onClose={() => {
              setShowShareFormModal(false);
              setSelectedFormToShare(null);
            }}
          />
        )}
        {formToDelete && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
            <div className="modal-dialog modal-sm modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Delete Form</h5>
                  <button type="button" className="btn-close" onClick={handleCancelDelete} disabled={deletePending} />
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    Do you wish to delete this {formToDelete.form?.name || ''}, Entered By {formToDelete.createdBy?.name || ''}?
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCancelDelete} disabled={deletePending}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={deletePending}>
                    {deletePending ? <span className="spinner-border spinner-border-sm" /> : 'OK'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FormHistoryLayer;
